import React, { FunctionComponent, useEffect } from 'react';
import { graphql } from 'react-relay';
import { usePaginationFragment } from 'react-relay/hooks';
import { CatalogListContent } from 'src/components/widgets/catalogList';
import { CatalogListItemModel } from 'src/components/widgets/catalogList/catalogListItemModel';
import { T } from 'src/shared/helpers';
import NotFoundIcon from 'src/shared/icons/icon-not-found.svg';
import { RelayFragment } from 'src/shared/types';
import { merchantProduct_item$data } from '../state/queries/__generated__/merchantProduct_item.graphql';
import { storeCatalogPaginationFragmentQuery } from './__generated__/storeCatalogPaginationFragmentQuery.graphql';
import { storeCatalogPaginationFragment__merchantProducts$key } from './__generated__/storeCatalogPaginationFragment__merchantProducts.graphql';
import { storeCatalogQuery$data } from './__generated__/storeCatalogQuery.graphql';

interface StoreCatalogPaginationFragmentProps {
    query: storeCatalogQuery$data;
    search: string;
    scrollContainer?: HTMLDivElement | null;
    selectedProductsSKUs?: string[];
    onProductClick: (merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>, hideAlreadyConfiguredBadge?: boolean) => void;
    hideAlreadyConfiguredBadge?: boolean;
    hideProductsNotConfigured?: boolean;
    disableProductCode?: string;
}

// List component
const StoreCatalogPaginationFragment: FunctionComponent<StoreCatalogPaginationFragmentProps> = (props) => {
    const { data, loadNext, hasNext, refetch, isLoadingNext } = usePaginationFragment<
        storeCatalogPaginationFragmentQuery,
        storeCatalogPaginationFragment__merchantProducts$key
    >(
        graphql`
            fragment storeCatalogPaginationFragment__merchantProducts on Query
            @refetchable(queryName: "storeCatalogPaginationFragmentQuery")
            @argumentDefinitions(
                count: { type: "Int", defaultValue: 20 }
                cursor: { type: "String", defaultValue: null }
                search: { type: "String", defaultValue: "" }
                salesChannelID: { type: "ID!" }
            ) {
                merchantProducts(first: $count, after: $cursor, search: $search, salesChannelID: $salesChannelID)
                    @connection(key: "storeCatalogPaginationFragment_merchantProducts") {
                    edges {
                        node {
                            ...merchantProduct_item @relay(mask: false)
                        }
                    }
                }
            }
        `,
        props.query
    );

    useEffect(() => {
        refetch({ count: 20, search: props.search }, { fetchPolicy: 'store-and-network' });
    }, [props.search]);

    const handleProductClick = (model: CatalogListItemModel, hideAlreadyConfiguredBadge: boolean | undefined) => {
        const merchantProduct = data.merchantProducts?.edges?.find((x) => x.node?.sku == model.subtitle);
        if (merchantProduct && merchantProduct.node) {
            props.onProductClick(merchantProduct.node, hideAlreadyConfiguredBadge);
        }
    };

    const products = props.hideProductsNotConfigured
        ? (data.merchantProducts?.edges?.filter((x) => x.node?.isConfiguratedInZakeke) ?? [])
        : (data.merchantProducts?.edges ?? []);

    return (
        <CatalogListContent
            scrollContainer={props.scrollContainer}
            models={products?.map((x) => ({
                thumbnail: x.node.thumbnail,
                title: x.node.name,
                subtitle: x.node.sku,
                isConfiguratedInZakeke: x.node.isConfiguratedInZakeke,
                id: x.node.sku,
                selected: x.node.sku ? props.selectedProductsSKUs?.indexOf(x.node.sku) !== -1 : false
            }))}
            loadNext={() => loadNext(20)}
            hasNext={hasNext}
            isLoadingNext={isLoadingNext}
            onItemClick={handleProductClick}
            noItemsIcon={<NotFoundIcon />}
            noItemsMessage={T._('No products found', 'Admin')}
            noItemsSubMessage={
                props.search !== ''
                    ? T._('Sorry, no product was found for your search. Please try using different keywords.', 'Admin')
                    : T._('Currently, there are no products available in this store. Please create at least one product in your store.', 'Admin')
            }
            hideAlreadyConfiguredBadge={props.hideAlreadyConfiguredBadge}
            disableProductCode={props.disableProductCode}
        />
    );
};

export default StoreCatalogPaginationFragment;
